/**
 * The hero's ZIP field: "do you clean here?" answered before the visitor
 * leaves this page, then a hand-off to the booking app with `?zip=`.
 *
 * The answer comes from the same endpoint the booking app's step 1 asks — a
 * ZIP outside the service area is a 400 whose `error` is the customer-facing
 * refusal ("We don't clean in 07030 yet. We clean in …") — so the two pages can
 * never disagree about coverage. Only the answer is read, not the calendar,
 * so one day is asked for.
 *
 * "Could not check" never blocks: the button stays live and the booking app
 * checks again. A marketing page that refuses a customer because an endpoint
 * was slow is worse than one that lets them through to be asked there.
 */

import React, { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';

/** Overridable so a local landing can hand off to a local booking app. */
const BOOKING_URL =
  process.env.REACT_APP_BOOKING_URL || 'https://booking.heyhomero.com';
const TEXT_US = 'sms:+19294974111';

type Coverage =
  | { status: 'idle' }
  | { status: 'checking' }
  | { status: 'served' }
  | { status: 'refused'; message: string }
  | { status: 'unreachable' };

/**
 * Today in New York, which is the calendar the business runs on, as
 * YYYY-MM-DD. Assembled from parts rather than trusting a locale's pattern: a
 * malformed date is a 400 from the endpoint, and a 400 reads as a refusal.
 */
function nyToday(): string {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: 'America/New_York',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).formatToParts(new Date());
  const part = (type: string) => parts.find((p) => p.type === type)?.value;
  return `${part('year')}-${part('month')}-${part('day')}`;
}

async function checkZip(zip: string, signal: AbortSignal): Promise<Coverage> {
  const day = nyToday();
  const params = new URLSearchParams({ zip, from: day, to: day });
  try {
    const response = await fetch(
      `${process.env.REACT_APP_API_URL}/pricing/proximity/dates?${params}`,
      { signal }
    );
    /**
     * A 400 is the service area saying no, with the refusal as `error`. The
     * endpoint's other 400s are for a malformed ZIP or dates, which this page
     * never sends; a 400 without a message is treated as "could not check"
     * rather than shown as a refusal we cannot word.
     */
    if (response.status === 400) {
      const body = await response.json().catch(() => null);
      return typeof body?.error === 'string'
        ? { status: 'refused', message: body.error }
        : { status: 'unreachable' };
    }
    if (!response.ok) return { status: 'unreachable' };
    // A 200 that is not our JSON (a proxy's HTML page) is "could not check".
    const body = await response.json().catch(() => null);
    return body && Array.isArray(body.dates)
      ? { status: 'served' }
      : { status: 'unreachable' };
  } catch (error) {
    if ((error as Error)?.name === 'AbortError') throw error;
    return { status: 'unreachable' };
  }
}

const Label = styled.label`
  display: block;
  margin: 0 0 8px;
  font-size: 0.85rem;
  font-weight: 600;
  color: #111827;
`;

const Row = styled.form<{ $refused: boolean }>`
  display: flex;
  max-width: 470px;
  padding: 5px;
  background: #fff;
  border: 1px solid ${(p) =>
    p.$refused ? '#c8322b' : '#d6d9e3'};
  border-radius: 8px;
  box-shadow: 0 1px 2px rgba(17, 24, 39, 0.06),
    0 2px 8px rgba(17, 24, 39, 0.05);

  &:focus-within {
    border-color: ${(p) =>
      p.$refused ? '#c8322b' : '#253ec6'};
  }

  @media (max-width: 600px) {
    flex-direction: column;
    gap: 6px;
    max-width: none;
  }
`;

const Field = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  flex: 1;
  min-width: 0;
  padding: 0 12px;

  svg {
    flex: none;
  }

  input {
    width: 100%;
    min-width: 0;
    height: 48px;
    border: 0;
    outline: none;
    background: transparent;
    font-size: 16px;
    font-weight: 500;
    letter-spacing: 0.02em;
    color: #111827;
    font-variant-numeric: tabular-nums;
  }

  input::placeholder {
    color: #575e75;
  }
`;

const Go = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  height: 48px;
  padding: 0 22px;
  border: 0;
  border-radius: 6px;
  background: #253ec6;
  color: #fff;
  font-size: 15px;
  font-weight: 600;
  white-space: nowrap;
  cursor: pointer;

  &:hover,
  &:focus-visible {
    background: #2e40a1;
  }

  &:disabled {
    background: #aeb6da;
    cursor: not-allowed;
  }
`;

const Status = styled.p<{ $tone: 'ok' | 'bad' | 'quiet' }>`
  display: flex;
  align-items: flex-start;
  gap: 6px;
  min-height: 22px;
  max-width: 470px;
  margin: 10px 0 0;
  font-size: 0.875rem;
  line-height: 1.45;
  color: ${(p) =>
    p.$tone === 'bad' ? '#c8322b' : p.$tone === 'ok' ? '#111827' : '#575e75'};

  svg {
    flex: none;
    margin-top: 2px;
  }

  a {
    color: #253ec6;
    font-weight: 600;
    white-space: nowrap;
  }
`;

const Pin = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden="true">
    <path
      fill="none"
      stroke="#575E75"
      strokeWidth="2"
      d="M12 21s-7-6.2-7-11.5A7 7 0 0 1 19 9.5C19 14.8 12 21 12 21z"
    />
    <circle cx="12" cy="9.5" r="2.5" fill="#575E75" />
  </svg>
);

const Arrow = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" aria-hidden="true">
    <path
      fill="none"
      stroke="currentColor"
      strokeWidth="2.2"
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M5 12h14M13 6l6 6-6 6"
    />
  </svg>
);

const Tick = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" aria-hidden="true">
    <path
      fill="none"
      stroke="#1F9D55"
      strokeWidth="2.6"
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M5 12.5l4.5 4.5L19 7.5"
    />
  </svg>
);

export default function ZipQuote() {
  const [zip, setZip] = useState('');
  const [coverage, setCoverage] = useState<Coverage>({ status: 'idle' });
  /** Get a Quote pressed without five digits. */
  const [incomplete, setIncomplete] = useState(false);
  const input = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (zip.length !== 5) {
      setCoverage({ status: 'idle' });
      return;
    }
    const controller = new AbortController();
    setCoverage({ status: 'checking' });
    checkZip(zip, controller.signal).then(
      (answer) => {
        /*
          An abort that lands while the body is being read is swallowed by
          `json().catch`, so the check still resolves — for a ZIP that has
          since changed. Only a check still current may answer.
        */
        if (!controller.signal.aborted) setCoverage(answer);
      },
      () => {
        // Aborted: a newer ZIP is being checked.
      }
    );
    return () => controller.abort();
  }, [zip]);

  const refused = coverage.status === 'refused';

  const submit = (event: React.FormEvent) => {
    event.preventDefault();
    if (zip.length !== 5) {
      setIncomplete(true);
      input.current?.focus();
      return;
    }
    if (refused) return;
    // Still checking goes through: the booking app asks again on arrival.
    window.location.href = `${BOOKING_URL}/?zip=${zip}`;
  };

  let status: React.ReactNode = null;
  if (incomplete && zip.length !== 5) {
    status = <Status $tone="bad">Enter your 5-digit ZIP code.</Status>;
  } else if (coverage.status === 'checking') {
    status = <Status $tone="quiet">Checking your ZIP…</Status>;
  } else if (coverage.status === 'served') {
    status = (
      <Status $tone="ok">
        <Tick />
        <span>We clean in {zip}.</span>
      </Status>
    );
  } else if (coverage.status === 'refused') {
    status = (
      <Status $tone="bad">
        <span>
          {coverage.message} Questions?{' '}
          <a href={TEXT_US}>Text us (929) 497-4111</a>
        </span>
      </Status>
    );
  } else if (coverage.status === 'unreachable') {
    status = (
      <Status $tone="quiet">
        We couldn’t check your ZIP right now. You can still continue.
      </Status>
    );
  }

  return (
    <div style={{ position: 'relative', zIndex: 10 }}>
      <Label htmlFor="hero-zip">Check if we clean in your area</Label>
      <Row $refused={refused} onSubmit={submit} noValidate>
        <Field>
          <Pin />
          <input
            ref={input}
            id="hero-zip"
            inputMode="numeric"
            autoComplete="postal-code"
            maxLength={5}
            placeholder="Enter your ZIP code"
            value={zip}
            onChange={(e) => {
              setIncomplete(false);
              setZip(e.target.value.replace(/\D/g, '').slice(0, 5));
            }}
            aria-invalid={refused || (incomplete && zip.length !== 5) || undefined}
            aria-describedby="hero-zip-status"
          />
        </Field>
        <Go type="submit" disabled={refused}>
          Get a Quote <Arrow />
        </Go>
      </Row>
      <div id="hero-zip-status" role="status">
        {status}
      </div>
    </div>
  );
}
